import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Home, Calendar, BookOpen, User, ChevronRight, Activity, Map } from 'lucide-react';
import { RealEstateApp } from './RealEstateApp';
import { HealthApp } from './HealthApp';
import { GastronomyApp } from './GastronomyApp';
import { LawApp } from './LawApp';
import { AutoApp } from './AutoApp';
import { ArchApp } from './ArchApp';
import { EcommerceApp } from './EcommerceApp';

interface AppDemoProps {
  prospect: Prospect;
}

export const AppDemo: React.FC<AppDemoProps> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (prospect.theme) {
      const root = document.documentElement;
      root.style.setProperty('--ym-prospect-primary', prospect.theme.primary_color);
      if (prospect.theme.bg_color) root.style.setProperty('--ym-prospect-bg', prospect.theme.bg_color);
      if (prospect.theme.text_color) root.style.setProperty('--ym-prospect-text', prospect.theme.text_color);
      if (prospect.theme.surface_color) root.style.setProperty('--ym-prospect-surface', prospect.theme.surface_color);
    }
  }, [prospect]);

  if (prospect.availableDemos?.includes('imobiliaria')) {
    return <RealEstateApp prospect={prospect} />;
  }
  
  if (prospect.availableDemos?.includes('clinica')) {
    return <HealthApp prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('gastronomia')) {
    return <GastronomyApp prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('advocacia')) {
    return <LawApp prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('automoveis')) {
    return <AutoApp prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('arquitetura')) {
    return <ArchApp prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('ecommerce')) {
    return <EcommerceApp prospect={prospect} />;
  }

  const isFitness = prospect.segment.toLowerCase().includes('fitness') || prospect.segment.toLowerCase().includes('academia') || prospect.segment.toLowerCase().includes('crossfit');

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      {/* Mobile Device Mockup */}
      <div className="w-[375px] h-[812px] bg-[var(--ym-prospect-bg)] text-[var(--ym-prospect-text)] rounded-[3rem] shadow-2xl relative overflow-hidden border-8 border-slate-800">
        
        {/* Dynamic Header */}
        <div className="px-6 pt-12 pb-6 bg-[var(--ym-prospect-primary)] text-white rounded-b-3xl shadow-md">
          <div className="flex justify-between items-center mb-6">
            <div>
              <p className="opacity-80 text-sm">Olá, Cliente</p>
              <h1 className="font-bold text-xl">{isFitness ? 'Seu bem-estar' : 'Sua próxima viagem'}</h1>
            </div>
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5" />
            </div>
          </div>
          
          {/* Main Card Header */}
          <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/20">
            {isFitness ? (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-80 mb-1">Seu Progresso</div>
                  <div className="font-bold text-xl">Nível Ouro</div>
                </div>
                <Activity className="w-8 h-8 opacity-50" />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm opacity-80 mb-1">Em 15 dias</div>
                  <div className="font-bold text-xl">Expedição Peru</div>
                </div>
                <Map className="w-8 h-8 opacity-50" />
              </div>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 h-full overflow-y-auto pb-32">
          
          <h2 className="font-bold text-lg mb-4">{isFitness ? 'Treino de Hoje' : 'Para sua viagem'}</h2>
          
          {/* Widgets */}
          <div className="space-y-4">
            
            {/* Widget 1 */}
            <div className="bg-[var(--ym-prospect-surface)] p-4 rounded-2xl flex items-center gap-4 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] flex items-center justify-center">
                {isFitness ? <Activity className="w-6 h-6" /> : <BookOpen className="w-6 h-6" />}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-sm">{isFitness ? 'WOD Principal' : 'Seus Vouchers'}</h3>
                <p className="text-xs opacity-60">{isFitness ? '45 min • Força e Condicionamento' : '3 documentos prontos'}</p>
              </div>
              <ChevronRight className="w-5 h-5 opacity-40" />
            </div>

            {/* Widget 2 */}
            <div className="bg-[var(--ym-prospect-surface)] p-4 rounded-2xl flex items-center gap-4 border border-black/5">
              <div className="w-12 h-12 rounded-xl bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] flex items-center justify-center">
                {isFitness ? <Calendar className="w-6 h-6" /> : <Calendar className="w-6 h-6" />}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-sm">{isFitness ? 'Check-in Liberado' : 'Itinerário'}</h3>
                <p className="text-xs opacity-60">{isFitness ? 'Turma das 19h00' : 'Dia 1: Chegada e Check-in'}</p>
              </div>
              <ChevronRight className="w-5 h-5 opacity-40" />
            </div>

          </div>

          <h2 className="font-bold text-lg mt-8 mb-4">Descubra</h2>
          <div className="flex gap-4 overflow-x-auto pb-4 snap-x">
            <div className="min-w-[200px] h-32 rounded-2xl bg-slate-200 snap-center relative overflow-hidden">
               <div className="absolute inset-0 bg-black/40" />
               <div className="absolute bottom-3 left-3 text-white font-bold text-sm">
                  {isFitness ? 'Novos Looks' : 'Destinos de Verão'}
               </div>
            </div>
            <div className="min-w-[200px] h-32 rounded-2xl bg-slate-200 snap-center relative overflow-hidden">
               <div className="absolute inset-0 bg-black/40" />
               <div className="absolute bottom-3 left-3 text-white font-bold text-sm">
                  {isFitness ? 'Clube de Benefícios' : 'Seguro Viagem'}
               </div>
            </div>
          </div>

        </div>

        {/* Bottom Nav */}
        <div className="absolute bottom-0 left-0 right-0 bg-[var(--ym-prospect-bg)] border-t border-black/5 flex justify-around p-4 pb-8">
          <div className="flex flex-col items-center text-[var(--ym-prospect-primary)]">
            <Home className="w-6 h-6" />
            <span className="text-[10px] mt-1 font-medium">Início</span>
          </div>
          <div className="flex flex-col items-center opacity-40">
            <Calendar className="w-6 h-6" />
            <span className="text-[10px] mt-1 font-medium">Agenda</span>
          </div>
          <div className="flex flex-col items-center opacity-40">
            <User className="w-6 h-6" />
            <span className="text-[10px] mt-1 font-medium">Perfil</span>
          </div>
        </div>

      </div>

      <div className="absolute top-10 right-10 text-white opacity-50 text-sm max-w-xs">
        <p className="font-bold mb-2">Conceito de App</p>
        <p>Wireframe demonstrativo do módulo mobile (Fase 6).</p>
      </div>
    </div>
  );
};
